// voxExportUtils.js
import * as THREE from 'three';

const VOX_VERSION = 150;

const createVoxHeader = () => {
    const buffer = new ArrayBuffer(8);
    const view = new DataView(buffer);
    view.setUint32(0, 0x20584f56, true); // 'VOX ' in ASCII
    view.setUint32(4, VOX_VERSION, true);
    return buffer;
};

const createMainChunk = (sizeChunks, xyziChunks, paletteChunk) => {
    const mainChunkID = new Uint8Array([0x4d, 0x41, 0x49, 0x4e]); // 'MAIN' in ASCII
    const contentSize = new Uint32Array([0]); // No content
    const childrenSize = new Uint32Array([sizeChunks.byteLength + xyziChunks.byteLength + paletteChunk.byteLength]);

    return concatBuffers(mainChunkID.buffer, contentSize.buffer, childrenSize.buffer, sizeChunks, xyziChunks, paletteChunk);
};

const createSizeChunk = (x, y, z) => {
    const buffer = new ArrayBuffer(24); // Corrected to allocate enough bytes
    const view = new DataView(buffer);
    view.setUint32(0, 0x455a4953, true); // 'SIZE' in ASCII
    view.setUint32(4, 12, true); // Content size
    view.setUint32(8, 0, true); // No children
    view.setUint32(12, x, true);
    view.setUint32(16, y, true);
    view.setUint32(20, z, true);
    console.log("Size chunk created:", new Uint8Array(buffer));
    return buffer;
};

const createXYZIChunk = (voxels) => {
    const buffer = new ArrayBuffer(16 + voxels.length * 4); // Corrected to allocate enough bytes
    const view = new DataView(buffer);
    view.setUint32(0, 0x495a5958, true); // 'XYZI' in ASCII
    view.setUint32(4, 4 + voxels.length * 4, true); // Content size
    view.setUint32(8, 0, true); // No children
    view.setUint32(12, voxels.length, true);
    voxels.forEach((voxel, index) => {
        view.setUint8(16 + index * 4, voxel.x);
        view.setUint8(16 + index * 4 + 1, voxel.y);
        view.setUint8(16 + index * 4 + 2, voxel.z);
        view.setUint8(16 + index * 4 + 3, voxel.colorIndex);
    });
    console.log("XYZI chunk created:", new Uint8Array(buffer));
    return buffer;
};

const createPaletteChunk = (palette) => {
    const buffer = new ArrayBuffer(1036); // Corrected to allocate enough bytes
    const view = new DataView(buffer);
    view.setUint32(0, 0x41424752, true); // 'RGBA' in ASCII
    view.setUint32(4, 256 * 4, true); // Content size
    view.setUint32(8, 0, true); // No children
    palette.forEach((color, index) => {
        const offset = 12 + index * 4;
        view.setUint8(offset, color.r);
        view.setUint8(offset + 1, color.g);
        view.setUint8(offset + 2, color.b);
        view.setUint8(offset + 3, color.a);
    });
    console.log("Palette chunk created:", new Uint8Array(buffer));
    return buffer;
};

const concatBuffers = (...buffers) => {
    const totalLength = buffers.reduce((acc, buffer) => acc + buffer.byteLength, 0);
    const tempBuffer = new Uint8Array(totalLength);
    let offset = 0;
    buffers.forEach(buffer => {
        tempBuffer.set(new Uint8Array(buffer), offset);
        offset += buffer.byteLength;
    });
    console.log("Buffers concatenated:", tempBuffer);
    return tempBuffer.buffer;
};

export const exportToVox = (groupRef) => {
    const voxels = [];
    const palette = new Map();
    let colorIndex = 1;  // Start color index at 1 (0 is typically transparent in vox format)

    // Extract all meshes including windows, vitrines and their frames
    const extractMeshes = (group) => {
        const meshes = [];
        console.log("Extracting from group:", group.name);
        
        // Statistics for debugging
        let normalMeshes = 0;
        let doorMeshes = 0;
        let windowMeshes = 0;
        let frameMeshes = 0;
        let vitrineMeshes = 0;
        
        // Recursively traverse the scene graph
        group.traverse((object) => {
            // Skip ground and non-visible objects
            if (object.name === 'ground' || (object.visible === false)) {
                return;
            }
            
            // Handle mesh objects with geometry and material
            if (object.isMesh && object.geometry && object.material) {
                // Special handling for various element types
                if (object.name.includes('window') || object.parent?.name?.includes('window')) {
                    windowMeshes++;
                } else if (object.name.includes('frame') || object.parent?.name?.includes('frame')) {
                    frameMeshes++;
                } else if (object.name.includes('door') || object.parent?.name?.includes('door')) {
                    doorMeshes++;
                } else if (object.name.includes('vitrine') || object.parent?.name?.includes('vitrine')) {
                    vitrineMeshes++;
                } else {
                    normalMeshes++;
                }
                
                meshes.push(object);
            }
        });
        
        console.log(`Found ${meshes.length} total meshes: ${normalMeshes} normal, ${windowMeshes} windows, ${frameMeshes} frames, ${doorMeshes} doors, ${vitrineMeshes} vitrines`);
        return meshes;
    };
    
    const buildingMeshes = extractMeshes(groupRef);
    
    // Calculate building bounds
    let minX = Infinity, minY = Infinity, minZ = Infinity;
    let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    
    // Calculate bounds by examining all mesh positions and geometries
    buildingMeshes.forEach(mesh => {
        // Only process mesh objects with valid geometry
        if (mesh.geometry) {
            const position = mesh.position;
            
            // Get geometry size - handle different geometry types
            let size = {
                x: 1, 
                y: 1, 
                z: 1
            };
            
            // BoxGeometry has simple parameters we can use
            if (mesh.geometry.type === 'BoxGeometry') {
                size = {
                    x: mesh.geometry.parameters.width || 1,
                    y: mesh.geometry.parameters.height || 1, 
                    z: mesh.geometry.parameters.depth || 1
                };
            }
            
            // Calculate corners of the mesh
            const halfX = size.x / 2;
            const halfY = size.y / 2;
            const halfZ = size.z / 2;
            
            // Update bounds with the mesh's extents
            minX = Math.min(minX, position.x - halfX);
            minY = Math.min(minY, position.y - halfY);
            minZ = Math.min(minZ, position.z - halfZ);
            maxX = Math.max(maxX, position.x + halfX);
            maxY = Math.max(maxY, position.y + halfY);
            maxZ = Math.max(maxZ, position.z + halfZ);
        }
    });
    
    // Calculate model dimensions
    const width = Math.ceil(maxX - minX);
    const height = Math.ceil(maxY - minY);
    const depth = Math.ceil(maxZ - minZ);
    
    // Ensure model fits within MagicaVoxel's 126³ limitation
    const scale = Math.max(width, height, depth) > 126 ? 
        126 / Math.max(width, height, depth) : 1;
        
    console.log(`Building dimensions: ${width}x${height}x${depth}`);
    console.log(`Using scale factor: ${scale}`);
    
    // Map for voxel position deduplication
    const voxelPositions = new Map();
    
    // Process each mesh to extract voxels - in specific order for proper overlapping
    // We want to process special elements like frames first, then windows/vitrines, then others
    // Reorder the meshes to prioritize frames and special elements
    const sortedMeshes = [...buildingMeshes].sort((a, b) => {
        // Define priority order (higher number = higher priority)
        const getPriority = (mesh) => {
            if (mesh.name.includes('vitrine_frame') || mesh.parent?.name?.includes('vitrine_frame')) return 100;
            if (mesh.name.includes('frame') || mesh.parent?.name?.includes('frame')) return 90;
            if (mesh.name.includes('window') || mesh.parent?.name?.includes('window')) return 80;
            if (mesh.name.includes('vitrine') || mesh.parent?.name?.includes('vitrine')) return 70;
            if (mesh.name.includes('door') || mesh.parent?.name?.includes('door')) return 60;
            return 0;
        };
        
        const priorityA = getPriority(a);
        const priorityB = getPriority(b);
        
        // Sort by descending priority
        return priorityB - priorityA;
    });
    
    // Debug all vitrine elements
    const vitrineElements = sortedMeshes.filter(mesh => 
        mesh.name.includes('vitrine') || mesh.parent?.name?.includes('vitrine')
    );
    console.log(`Found ${vitrineElements.length} vitrine elements:`, 
        vitrineElements.map(v => ({
            name: v.name, 
            position: {x: v.position.x, y: v.position.y, z: v.position.z},
            material: v.material?.color?.getHexString()
        }))
    );
    
    // Add a special processing step for vitrine elements which often get overlooked
    // This will simulate the vitrine elements at positions where they should be
    // Based on the building's configuration
    const addExtraVitrineElements = () => {
        // Only if we have few or no vitrines but we know vitrine should be enabled
        if (vitrineElements.length < 10) {
            console.log("Adding extra vitrine elements for better export...");
            
            // Look for the back facade position (depth-1)
            let backFacadeZ = -1;
            let doorHeight = 0;
            let doorWidth = 0;
            let doorX = 0;
            let maxY = 0;
            let vitrineOffset = 0;
            
            // Find the back facade Z position and door parameters from existing elements
            buildingMeshes.forEach(mesh => {
                if (mesh.position.z > backFacadeZ) {
                    backFacadeZ = Math.round(mesh.position.z);
                }
                if (mesh.position.y > maxY) {
                    maxY = Math.round(mesh.position.y);
                }
                // Look for door elements to determine door parameters
                if (mesh.name.includes('door') && mesh.position.z === backFacadeZ) {
                    doorHeight = Math.max(doorHeight, Math.round(mesh.position.y) + 1);
                    doorX = Math.round(mesh.position.x);
                    doorWidth = Math.max(doorWidth, Math.round(mesh.geometry.parameters?.width || 3));
                }
                // Detect vitrineOffset from existing vitrines
                if (mesh.name.includes('vitrine') && mesh.position.z === backFacadeZ) {
                    vitrineOffset = Math.min(vitrineOffset || 999, Math.round(mesh.position.x));
                }
            });
            
            // Use some reasonable defaults if we couldn't determine
            doorHeight = doorHeight || 7;
            doorWidth = doorWidth || 3;
            doorX = doorX || 20; // Center for width 42
            vitrineOffset = vitrineOffset || 1;
            
            const width = 42; // Standard width
            
            // Generate simulated vitrine elements for the back facade
            const fillColor = new THREE.Color(0x00aaee); // Default fill color (blue)
            const frameColor = new THREE.Color(0x666666); // Default frame color (gray)
            
            // Add vitrine cubes all along the back facade except at door
            for (let y = 0; y < doorHeight; y++) {
                for (let x = vitrineOffset; x < width - vitrineOffset; x++) {
                    // Skip the door area
                    if (x >= doorX - vitrineOffset && x < doorX + doorWidth + vitrineOffset) {
                        continue;
                    }
                    
                    // Create the position for the vitrine voxel
                    const position = new THREE.Vector3(x, y, backFacadeZ);
                    
                    // Determine if this is a frame position
                    const isFramePosition = 
                        x === vitrineOffset || // Left edge
                        x === width - vitrineOffset - 1 || // Right edge
                        y === 0 || // Bottom edge
                        y === doorHeight - 1 || // Top edge
                        x === doorX - vitrineOffset - 1 || // Left door edge
                        x === doorX + doorWidth + vitrineOffset; // Right door edge
                    
                    // Create simulated mesh for the voxel exporter
                    const simulatedMesh = {
                        isMesh: true,
                        visible: true,
                        name: isFramePosition ? 'vitrine_frame_element' : 'vitrine_element',
                        position: position,
                        geometry: { 
                            type: 'BoxGeometry',
                            parameters: { width: 1, height: 1, depth: 1 }
                        },
                        material: {
                            color: isFramePosition ? frameColor : fillColor,
                            // Add getHexString method since we'll use it for simulated meshes
                            getHexString: function() {
                                return isFramePosition ? '666666' : '00aaee';
                            }
                        }
                    };
                    
                    // Add to our list of meshes to process
                    sortedMeshes.unshift(simulatedMesh);
                }
            }
        }
    };
    
    // Try to add extra vitrine elements if needed
    addExtraVitrineElements();
    
    // Process meshes in priority order to ensure frames and special elements are included
    sortedMeshes.forEach(mesh => {
        // Skip invisible meshes and meshes without material
        if (!mesh.visible || !mesh.material || !mesh.material.color) return;
        
        // Force vitrine elements to be processed with special treatment
        // This ensures they are always included even if other elements overlap
        const isVitrineElement = mesh.name && mesh.name.includes('vitrine');
        
        const material = mesh.material;
        const color = material.color;
        const hexColor = color.getHexString ? color.getHexString() : 
                         (typeof color === 'string' ? color : '00aaee'); // Fallback color
        
        // Add to color palette if not already there
        if (!palette.has(hexColor)) {
            // Limit to 255 colors (index 0-254, 255 is typically reserved)
            if (colorIndex < 255) {
            palette.set(hexColor, colorIndex++);
            }
        }
        
        // Determine if this is a special element that should override others
        const isVitrineFrame = mesh.name && mesh.name.includes('vitrine_frame');
        const isVitrine = isVitrineElement && !isVitrineFrame;
        
        // Force lower elements to take precedence over higher elements
        // This ensures that ground floor elements are not overshadowed by upper floors
        const isGroundFloorElement = mesh.position.y < 10;
        
        // Handle different geometry types
        if (mesh.geometry && mesh.geometry.type === 'BoxGeometry') {
            // For all geometry processing, ensure vitrine elements get special treatment
            // Get geometry size
            const size = {
                x: mesh.geometry.parameters.width || 1,
                y: mesh.geometry.parameters.height || 1, 
                z: mesh.geometry.parameters.depth || 1
            };
            
            // For non-unit cubes (doors, etc), voxelize them
            if (size.x > 1 || size.y > 1 || size.z > 1) {
                // Calculate corners of the mesh
                const halfX = size.x / 2;
                const halfY = size.y / 2;
                const halfZ = size.z / 2;
                
                // Voxelize the mesh by filling its volume with unit cubes
                const startX = mesh.position.x - halfX;
                const startY = mesh.position.y - halfY;
                const startZ = mesh.position.z - halfZ;
                
                // Slight adjustment to ensure we get integer positions
                const numX = Math.round(size.x);
                const numY = Math.round(size.y);
                const numZ = Math.round(size.z);
                
                // Create voxels for the entire volume
                for (let x = 0; x < numX; x++) {
                    for (let y = 0; y < numY; y++) {
                        for (let z = 0; z < numZ; z++) {
                            const worldX = Math.round(startX + x);
                            const worldY = Math.round(startY + y);
                            const worldZ = Math.round(startZ + z);
                            
                            // Calculate voxel position, scaled and offset to fit in VOX file
                            const voxX = Math.floor((worldX - minX) * scale);
                            const voxY = Math.floor((worldY - minY) * scale);
                            const voxZ = Math.floor((worldZ - minZ) * scale);
                            
                            // Create a unique position key
                            const posKey = `${voxX},${voxY},${voxZ}`;
                            
                            // Check if there's already a voxel at this position
                            const existingVoxel = voxelPositions.get(posKey);
                            
                            // Decide whether to add/replace the voxel based on priority
                            let shouldAddVoxel = false;
                            
                            // Vitrine frames have highest priority
                            if (isVitrineFrame) {
                                shouldAddVoxel = true;
                            }
                            // Vitrine fills have high priority but don't override frames
                            else if (isVitrine && (!existingVoxel || existingVoxel.priority !== 'frame')) {
                                shouldAddVoxel = true;
                            }
                            // Ground floor elements get higher priority for visibility
                            else if (isGroundFloorElement && (!existingVoxel || existingVoxel.priority === 'regular')) {
                                shouldAddVoxel = true;
                            }
                            // Otherwise only add if position is empty
                            else if (!existingVoxel) {
                                shouldAddVoxel = true;
                            }
                            
                            if (shouldAddVoxel) {
                                const priority = isVitrineFrame ? 'frame' : 
                                                (isVitrine ? 'vitrine' : 
                                                (isGroundFloorElement ? 'ground' : 'regular'));
                                
                                voxelPositions.set(posKey, {
                                    x: voxX, y: voxY, z: voxZ,
                                    colorIndex: palette.get(hexColor),
                                    priority
                                });
                            }
                        }
                    }
                }
            } else {
                // For regular unit cubes
                // Calculate voxel position, scaled and offset to fit in VOX file
                const voxX = Math.floor((mesh.position.x - minX) * scale);
                const voxY = Math.floor((mesh.position.y - minY) * scale);
                const voxZ = Math.floor((mesh.position.z - minZ) * scale);
                
                // Create a unique position key
                const posKey = `${voxX},${voxY},${voxZ}`;
                
                // Check if there's already a voxel at this position
                const existingVoxel = voxelPositions.get(posKey);
                
                // Decide whether to add/replace the voxel based on priority
                let shouldAddVoxel = false;
                
                // Vitrine frames have highest priority
                if (isVitrineFrame) {
                    shouldAddVoxel = true;
                }
                // Vitrine fills have high priority but don't override frames
                else if (isVitrine && (!existingVoxel || existingVoxel.priority !== 'frame')) {
                    shouldAddVoxel = true;
                }
                // Ground floor elements get higher priority for visibility
                else if (isGroundFloorElement && (!existingVoxel || existingVoxel.priority === 'regular')) {
                    shouldAddVoxel = true;
                }
                // Otherwise only add if position is empty
                else if (!existingVoxel) {
                    shouldAddVoxel = true;
                }
                
                if (shouldAddVoxel) {
                    const priority = isVitrineFrame ? 'frame' : 
                                    (isVitrine ? 'vitrine' : 
                                    (isGroundFloorElement ? 'ground' : 'regular'));
                    
                    voxelPositions.set(posKey, {
                        x: voxX, y: voxY, z: voxZ,
                        colorIndex: palette.get(hexColor),
                        priority
                    });
                }
            }
        }
    });
    
    // Log statistics of voxel types
    const voxelStats = {
        frames: 0,
        vitrines: 0,
        ground: 0,
        regular: 0,
        total: 0
    };
    
    voxelPositions.forEach(voxel => {
        voxelStats.total++;
        if (voxel.priority === 'frame') voxelStats.frames++;
        else if (voxel.priority === 'vitrine') voxelStats.vitrines++;
        else if (voxel.priority === 'ground') voxelStats.ground++;
        else voxelStats.regular++;
    });
    
    console.log('Voxel statistics:', voxelStats);
    
    // Convert the Map to an array of voxels for the export
    // Make sure we're only including the position and colorIndex
    const uniqueVoxels = Array.from(voxelPositions.values()).map(({ x, y, z, colorIndex }) => ({
        x, y, z, colorIndex
    }));
    console.log(`Exporting ${uniqueVoxels.length} unique voxels`);
    
    // Create palette array
    const paletteArray = Array.from(palette.keys()).map(hex => {
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return { r, g, b, a: 255 };
    });

    // Pad palette to 256 colors
    while (paletteArray.length < 256) {
        paletteArray.push({ r: 0, g: 0, b: 0, a: 0 });
    }

    // Adjusted model size for the VOX file
    const modelSize = Math.ceil(Math.max(width, height, depth) * scale);
    
    // Create file components
    const header = createVoxHeader();
    const sizeChunk = createSizeChunk(modelSize, modelSize, modelSize);
    const xyziChunk = createXYZIChunk(uniqueVoxels);
    const paletteChunk = createPaletteChunk(paletteArray);
    const mainChunk = createMainChunk(sizeChunk, xyziChunk, paletteChunk);

    // Combine into final buffer
    const finalBuffer = concatBuffers(header, mainChunk);

    // Create and trigger download
    const blob = new Blob([finalBuffer], { type: "application/octet-stream" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "nounstone_building.vox";
    link.click();
};